import { PrinterOutlined, RightOutlined } from '@ant-design/icons';
import { Alert, Button, Select } from 'antd';
import { addDays, format, subDays } from 'date-fns';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import DailyRoster from '..';
import { useNotify } from '../../../hooks/useNotify';
import useQueryTeamIds from '../../../hooks/useQueryTeamIds';
import useQueryTeamRoster from '../../../hooks/useQueryTeamRoster';
import { onApolloError } from '../../../lib/graphql/helpers';
import { truncate } from '../../../lib/utils';
import {
  asDateNumber,
  rosterLongHeaderDateFormat,
} from '../../../lib/utils/date-time';
import DatePicker from '../../DatePicker';
import HeaderSection from '../../HeaderSection';
import PageLoadingSpinner from '../../PageLoadingSpinner';

// The size of an A3 Page in mm
const a3Dimensions = { height: 297, width: 420 } as const;

/**
 * Given a width, returns the relative height for an A3 page.
 */
const a3Height = (width: number) =>
  // eslint-disable-next-line total-functions/no-partial-division
  width * (a3Dimensions.height / a3Dimensions.width);

const now = new Date();

const Roster: React.FC<{ readonly id: string; readonly rosterDate: Date }> = ({
  id,
  rosterDate,
}) => {
  const { notify } = useNotify();
  const { data } = useQueryTeamRoster(
    subDays(rosterDate, 1),
    addDays(rosterDate, 1),
    onApolloError(notify.error),
    id
  );

  const teamRoster = data?.queryTeamRoster?.at(0);
  const [height, setHeight] = useState(0);

  // Keep the dimensions of the PDF Viewer at A3 proportions to always display the whole page
  // (i.e. avoid immediately having to scroll the viewer)
  const rosterRef = useCallback((node: HTMLDivElement | null) => {
    if (node === null) {
      return;
    }
    new ResizeObserver(() =>
      // Add 50px to the height to account for the PDF Viewer toolbar
      setHeight(a3Height(node.offsetWidth) + 50)
    ).observe(node);
  }, []);

  if (teamRoster === undefined) {
    return <PageLoadingSpinner />;
  }

  return (
    <div ref={rosterRef} style={{ width: '100%', height }}>
      <DailyRoster team={teamRoster} date={asDateNumber(rosterDate)} />
    </div>
  );
};

const maxLengthBeforeEllipse = 20;

const DailyRosterViewer = () => {
  const { notify } = useNotify();
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(
    undefined
  );
  const [date, setDate] = useState<Date>(now);
  const { data, loading } = useQueryTeamIds(onApolloError(notify.error));

  const [search, setSearch] = useState('');

  if (loading) {
    return <PageLoadingSpinner />;
  }

  const selectedTeamName = data?.team.find(
    (team) => team.id === selectedTeamId
  )?.name;

  return (
    <div className="daily-roster-viewer">
      <HeaderSection
        title={`${format(date, rosterLongHeaderDateFormat)} Roster`}
        endElement={
          selectedTeamId === undefined || selectedTeamName === undefined ? (
            <></>
          ) : (
            <Link to={`/teams/${selectedTeamId}`}>
              <Button id="go-to-team-button" style={{ alignSelf: 'end' }}>
                {truncate(selectedTeamName, maxLengthBeforeEllipse, true)}
                {' Roster'}
                <RightOutlined />
              </Button>
            </Link>
          )
        }
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          minWidth: '500px',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 10 }}>
          <h3
            style={{
              paddingRight: 5,
              whiteSpace: 'nowrap',
              margin: 0,
              fontSize: 25,
            }}
          >{`${
            selectedTeamId === undefined ? 'Display' : 'Displaying'
          } roster for team:`}</h3>
          <Select
            className="daily-roster-team-select"
            style={{ minWidth: 200, maxWidth: 500, height: 40 }}
            showSearch
            optionFilterProp="children"
            onChange={setSelectedTeamId}
            onSearch={setSearch}
            searchValue={search}
            filterOption={(input, option) =>
              option?.label.toLowerCase().includes(input.toLowerCase()) ?? false
            }
            options={
              data?.team.map(({ id, name }) => ({ value: id, label: name })) ??
              []
            }
          />
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          minWidth: '500px',
          marginBottom: 10,
        }}
      >
        <h3
          style={{
            paddingRight: 5,
            whiteSpace: 'nowrap',
            margin: 0,
            fontSize: 25,
          }}
        >
          Roster Date:
        </h3>
        <DatePicker
          id="export-range-picker"
          allowClear={false}
          value={date}
          onChange={(value) => (value !== null ? setDate(value) : undefined)}
        />
      </div>
      {selectedTeamId === undefined ? (
        <></>
      ) : (
        <>
          <div
            style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <Alert
              message={
                <>
                  {`To save the PDF with its title, click `}
                  <PrinterOutlined />
                  {` and `}
                  <em>Save as PDF</em>
                </>
              }
              type="info"
              style={{ width: 'fit-content', marginBottom: 10 }}
              showIcon
              closable
            />
          </div>

          <Roster id={selectedTeamId} rosterDate={date} />
        </>
      )}
    </div>
  );
};

export default DailyRosterViewer;
